import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  ExploreResults,
  PostExploreNoInsightsRequestFilters,
  PostExploreRequestFilters,
  SortByParams,
} from '@toggle/toggle';

import {
  fetchExploreNoInsightsResults,
  fetchExploreNoMatchedResults,
  fetchExploreResults,
} from '~/views/insights/insights-services/insights-service';

export const QUERY_KEY_EXPLORE = 'QUERY_KEY_EXPLORE';

export const useExplore = ({
  entityTags,
  filters,
  sortBy,
  combined,
  pageSize = 20,
}: {
  entityTags: string[];
  filters: PostExploreRequestFilters;
  sortBy?: SortByParams;
  combined: boolean;
  pageSize?: number;
}) =>
  useInfiniteQuery<ExploreResults>({
    queryKey: [
      QUERY_KEY_EXPLORE,
      entityTags,
      filters,
      combined,
      pageSize,
      sortBy,
    ],
    queryFn: async ({ signal, pageParam }) =>
      fetchExploreResults({
        filters,
        sortBy,
        combined,
        page: {
          number: pageParam as number,
          size: pageSize,
        },
        signal,
      }),
    initialPageParam: 1,
    enabled: !!entityTags.length,
    getNextPageParam: lastPage => lastPage.page.next,
    staleTime: getTimeTillEndOfDayMs(),
  });

export const QUERY_KEY_EXPLORE_NOT_MATCHED = 'QUERY_KEY_EXPLORE_NOT_MATCHED';

export const useExploreNotMatched = ({
  filters,
  pageSize = 20,
}: {
  filters: PostExploreRequestFilters;
  pageSize?: number;
}) =>
  useInfiniteQuery<ExploreResults>({
    queryKey: [QUERY_KEY_EXPLORE_NOT_MATCHED, filters, pageSize],
    queryFn: async ({ signal, pageParam }) =>
      fetchExploreNoMatchedResults({
        filters,
        page: {
          number: pageParam as number,
          size: pageSize,
        },
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.page.next,
    staleTime: getTimeTillEndOfDayMs(),
  });

export const QUERY_KEY_EXPLORE_NO_INSIGHTS = 'QUERY_KEY_EXPLORE_NO_INSIGHTS';

export const useExploreNoInsights = ({
  filters,
}: {
  filters: PostExploreNoInsightsRequestFilters;
  initialPageParam?: number;
}) =>
  useQuery({
    queryKey: [QUERY_KEY_EXPLORE_NO_INSIGHTS, filters],
    queryFn: ({ signal }) => fetchExploreNoInsightsResults({ filters, signal }),
    staleTime: getTimeTillEndOfDayMs(),
  });
